import { create } from 'zustand';
import type { LoginResponse } from '@timesheet/shared';
import {
  clearAllAuth,
  getStoredUser,
  getToken,
  setStoredUser,
  setToken,
} from '@/lib/storage';

type AuthUser = LoginResponse['user'];

interface AuthState {
  /** Current authenticated user, or null if not logged in. */
  user: AuthUser | null;
  /** True until the initial localStorage rehydration finishes. */
  hydrated: boolean;

  /** Persist + set both token and user after a successful login. */
  login: (response: LoginResponse) => void;
  /** Clear both store + storage. */
  logout: () => void;
  /** Update just the user object (e.g. after /auth/me refresh). */
  setUser: (user: AuthUser) => void;
}

/**
 * Auth state — single source of truth for "is the user logged in".
 *
 * Persistence: token + user are mirrored to localStorage via `storage.ts`
 * so a page reload restores the session. The actual JWT is validated by
 * the server on every request, so a tampered localStorage just yields a 401.
 */
export const useAuthStore = create<AuthState>((set) => ({
  user: getStoredUser<AuthUser>() ?? null,
  hydrated: true, // synchronous read from localStorage, so already hydrated

  login: (response) => {
    setToken(response.token);
    setStoredUser(response.user);
    set({ user: response.user });
  },

  logout: () => {
    clearAllAuth();
    set({ user: null });
  },

  setUser: (user) => {
    setStoredUser(user);
    set({ user });
  },
}));

/** Quick boolean selector — `useAuthStore(selectIsAuthed)`. */
export const selectIsAuthed = (s: AuthState) => s.user !== null && getToken() !== undefined;
